import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import Login from './pages/Login';
import Profile from './pages/Profile';
import VerifyEmail from './pages/VerifyEmail';
import LoginLink from './pages/LoginLink';

// Facebook Pixel
import { initFBPixel, trackPageView } from './utils/fbPixel';

// Info pages
import About from './pages/About';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Shipping from './pages/Shipping';
import ReturnsPolicy from './pages/ReturnsPolicy';
import OrderTracking from './pages/OrderTracking';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import Disclaimer from './pages/Disclaimer';

// Admin pages
import {
  AdminLogin,
  AdminLayout,
  AdminDashboard,
  AdminProducts,
  AdminProductForm,
  AdminOrders,
  AdminUsers,
  AdminCoupons,
  AdminReviews,
  AdminBanners,
  AdminContent,
  AdminInventory
} from './pages/admin';
import AdminExportImport from './pages/admin/AdminExportImport';
import AdminPOSLocations from './pages/admin/AdminPOSLocations';
import AdminPOS from './pages/admin/AdminPOS';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminTools from './pages/admin/AdminTools';
import AdminBrands from './pages/admin/AdminBrands';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminPromos from './pages/admin/AdminPromos';
import AdminInfluencers from './pages/admin/AdminInfluencers';
import AdminCustomers from './pages/admin/AdminCustomers';

// Influencer pages
import InfluencerLogin from './pages/InfluencerLogin';
import InfluencerDashboard from './pages/InfluencerDashboard';

import './App.css';

// Facebook Pixel PageView tracker component
const FacebookPixelTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Initialize pixel on mount
    initFBPixel();
  }, []);
  
  useEffect(() => {
    // Track page view on route change
    trackPageView();
  }, [location.pathname]);
  
  return null;
};

// Wrapper component for public pages
const PublicPage = ({ children }) => (
  <Layout>{children}</Layout>
);

function App() {
  return (
    <BrowserRouter>
      <FacebookPixelTracker />
      <ScrollToTop />
      <AuthProvider>
        <CartProvider>
          <AdminAuthProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<PublicPage><Home /></PublicPage>} />
              <Route path="/products" element={<PublicPage><Products /></PublicPage>} />
              {/* Enlace corto para compartir: solo los perfumes marcados como "Nuevo Lanzamiento". */}
              <Route path="/novedades" element={<Navigate to="/products?popularity=Nuevo Lanzamiento" replace />} />
              <Route path="/products/:id" element={<PublicPage><ProductDetail /></PublicPage>} />
              <Route path="/verificar-email/:token" element={<PublicPage><VerifyEmail /></PublicPage>} />
              <Route path="/cart" element={<PublicPage><Cart /></PublicPage>} />
              <Route path="/checkout" element={<PublicPage><Checkout /></PublicPage>} />
              <Route path="/checkout/success" element={<PublicPage><CheckoutSuccess /></PublicPage>} />
              <Route path="/login" element={<PublicPage><Login /></PublicPage>} />
              <Route path="/entrar/:token" element={<PublicPage><LoginLink /></PublicPage>} />
              <Route path="/profile" element={<PublicPage><Profile /></PublicPage>} />

              {/* Info pages */}
              <Route path="/about" element={<PublicPage><About /></PublicPage>} />
              <Route path="/faq" element={<PublicPage><FAQ /></PublicPage>} />
              <Route path="/contact" element={<PublicPage><Contact /></PublicPage>} />
              <Route path="/shipping" element={<PublicPage><Shipping /></PublicPage>} />
              <Route path="/returns" element={<PublicPage><ReturnsPolicy /></PublicPage>} />
              <Route path="/order-tracking" element={<PublicPage><OrderTracking /></PublicPage>} />
              <Route path="/privacy" element={<PublicPage><PrivacyPolicy /></PublicPage>} />
              <Route path="/terms" element={<PublicPage><Terms /></PublicPage>} />
              <Route path="/cookies" element={<PublicPage><Cookies /></PublicPage>} />
              <Route path="/disclaimer" element={<PublicPage><Disclaimer /></PublicPage>} />

              {/* Admin routes - separate layout */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/export-import" element={<AdminExportImport />} />
                <Route path="products/:id" element={<AdminProductForm />} />
                <Route path="inventory" element={<AdminInventory />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="content" element={<AdminContent />} />
                <Route path="pos" element={<AdminPOS />} />
                <Route path="pos/locations" element={<AdminPOSLocations />} />
                <Route path="announcements" element={<AdminAnnouncements />} />
                <Route path="tools" element={<AdminTools />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="promos" element={<AdminPromos />} />
                <Route path="influencers" element={<AdminInfluencers />} />
                <Route path="customers" element={<AdminCustomers />} />
              </Route>

              {/* Influencer routes */}
              <Route path="/influencers" element={<InfluencerLogin />} />
              <Route path="/influencers/dashboard" element={<InfluencerDashboard />} />
            </Routes>
            <Toaster position="bottom-right" />
          </AdminAuthProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
